import React, { Component, KeyboardEventHandler, createRef } from 'react';
import classNames from 'classnames';
import styles from './autocomplete.styl';
import { Input, InputProps } from '../input/input';
import pointerIcon from '!!url-loader!./img/autocomplete.png';

enum Keys {
  Up = 38,
  Down = 40,
  Enter = 13,
}

export interface AutocompleteItem {
  label: string;
  value: string;
}

type SupportedInputProps = Pick<
  InputProps,
  | 'disabled'
  | 'errorMessage'
  | 'hasError'
  | 'id'
  | 'onBlur'
  | 'onChange'
  | 'onFocus'
  | 'placeholder'
  | 'showRequiredMark'
  | 'title'
  | 'value'
  | 'autoFocus'
>;

export interface AutocompleteProps extends SupportedInputProps {
  isOpen: boolean;
  minSymbolsCountToStartSearch?: number;
  suggestions: AutocompleteItem[];
  onSelect?: (value: AutocompleteItem) => void;

  highlightIndex?: number;
  onHighlightIndexChange?: (index: number) => void;
  onNextSuggestion?: () => void;
  onPrevSuggestion?: () => void;
}

interface AutocompleteState {
  index: number;
}

export class Autocomplete extends Component<AutocompleteProps, AutocompleteState> {
  private listRef = createRef<HTMLUListElement>();
  private isHoverBlocked: boolean = false;
  private inputRef = createRef<Input>();

  readonly state = {
    index: -1,
  };

  // adjusts viewport after highlight index change
  private adjustViewport = () => {
    if (!this.listRef.current) {
      return;
    }

    const highlightedElement: HTMLUListElement =
      this.listRef.current.children &&
      (this.listRef.current.children[this.highlightedSuggestionIndex()] as HTMLUListElement);

    // if we have bad highlight index
    if (!highlightedElement) {
      return;
    }

    // block hover to not trigger hover event on scroll
    // use setTimeout to unblock it after scrolling is done (0 is not enough)
    this.isHoverBlocked = true;
    this.adjustViewportItem(highlightedElement);
    setTimeout(() => (this.isHoverBlocked = false), 100);
  };

  private adjustViewportItem = (el: HTMLUListElement) => {
    const offsetTop = el.offsetTop;
    const height = el.offsetHeight;

    const parentElement = el.parentElement;

    if (!parentElement) {
      return;
    }

    const parentHeight = parentElement.offsetHeight;
    const parentScrollTop = parentElement.scrollTop;

    if (offsetTop < parentScrollTop) {
      parentElement.scrollTop = parentScrollTop - height;
      return;
    }

    if (parentScrollTop + parentHeight < offsetTop + height) {
      parentElement.scrollTop = parentScrollTop + height;
      return;
    }
  };

  private onChange = (value: string) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(value);
    }

    this.setState({ index: -1 });
  };

  private onSelect = (selectedItem: AutocompleteItem) => {
    const { onSelect } = this.props;
    if (onSelect) {
      onSelect(selectedItem);
    }

    this.setState({ index: -1 });
  };

  private onSuggestionHover = (index: number) => {
    if (this.isHoverBlocked) {
      return;
    }

    if (this.props.onHighlightIndexChange) {
      this.props.onHighlightIndexChange(index);
    }

    this.setState({ index });
  };

  private onNextSuggestion = () => {
    if (this.props.onNextSuggestion) {
      this.props.onNextSuggestion();
    }

    if (this.state.index < this.props.suggestions.length - 1) {
      this.setState({ index: this.state.index + 1 });
    }

    setTimeout(this.adjustViewport, 0);
  };

  private onPrevSuggestion = () => {
    if (this.props.onPrevSuggestion) {
      this.props.onPrevSuggestion();
    }

    if (this.state.index > 0) {
      this.setState({ index: this.state.index - 1 });
    }

    setTimeout(this.adjustViewport, 0);
  };

  private onSelectSuggestion = () => {
    this.onSelect(this.props.suggestions[this.highlightedSuggestionIndex()]);
  };

  private highlightedSuggestionIndex = () => {
    return typeof this.props.highlightIndex === 'number'
      ? this.props.highlightIndex
      : this.state.index;
  };

  private renderOptions = () => {
    const { suggestions, minSymbolsCountToStartSearch, value } = this.props;
    const minSymbolsToStartSearch = minSymbolsCountToStartSearch || 0;

    if (suggestions.length === 0) {
      return null;
    }

    if (!value) {
      return null;
    }

    if (value.length < minSymbolsToStartSearch) {
      return null;
    }

    return (
      <div className={classNames(styles.categories)}>
        <ul className={classNames(styles.itemsContainer)} ref={this.listRef}>
          {suggestions.map((item: AutocompleteItem, index) => (
            <li
              key={item.value}
              onClick={() => this.onSelect(item)}
              onMouseEnter={() => this.onSuggestionHover(index)}
              className={classNames(styles.suggestion, {
                [styles.isHighlighed]: index === this.highlightedSuggestionIndex(),
              })}
            >
              {item.label}
            </li>
          ))}
        </ul>
        <img className={classNames(styles.pointer)} src={pointerIcon} />
      </div>
    );
  };

  private onKeyDown: KeyboardEventHandler<HTMLInputElement> = event => {
    const { which } = event;

    switch (which) {
      case Keys.Down:
        // prevent cursor movement in the input
        event.preventDefault();
        this.onNextSuggestion();
        return;
      case Keys.Up:
        // prevent cursor movement in the input
        event.preventDefault();
        this.onPrevSuggestion();
        return;
      case Keys.Enter:
        this.onSelectSuggestion();
        return;
    }
  };

  public focus = () => {
    if (!this.inputRef.current) {
      return;
    }

    this.inputRef.current.focus();
  };

  render() {
    const {
      id,
      title,
      value,
      isOpen,
      hasError,
      errorMessage,
      showRequiredMark,
      onBlur,
      onFocus,
      disabled,
      autoFocus,
    } = this.props;

    return (
      <div className={classNames(styles.root)}>
        <Input
          ref={this.inputRef}
          id={id}
          title={title}
          inputType="alternate"
          type="search"
          hasError={hasError}
          value={value}
          onChange={this.onChange}
          onFocus={onFocus}
          errorMessage={errorMessage}
          showRequiredMark={showRequiredMark}
          disabled={disabled}
          onKeyDown={this.onKeyDown}
          autoFocus={autoFocus}
          onBlur={event => {
            /* Hack related to the issue

            https://stackoverflow.com/questions/121499/when-a-blur-event-occurs-how-can-i-find-out-which-element-focus-went-to?noredirect=1&lq=1

            looks like 'setTimeout' is the best cross browser solution for now because of poor support of 'relatedTarget' on mobile platforms */
            setTimeout(() => {
              if (onBlur) {
                onBlur(event);
              }
            }, 300);
          }}
        />
        {isOpen && this.renderOptions()}
      </div>
    );
  }
}
